/**
 * Work Item Flat List - Simple list view sorted by priority
 *
 * Features:
 * - Flat list (no grouping)
 * - Sorted by priority (P0 first) then due_date
 * - Quick scan of all items
 */

import React, { useMemo } from 'react';
import { ListChecks } from 'lucide-react';
import WorkItemRow from './WorkItemRow';
import { PRIORITY_COLORS } from './pipelineConfig';

/**
 * Sort items by priority then due_date
 */
function sortItems(items) {
  const priorityOrder = { P0: 0, P1: 1, P2: 2, P3: 3 };

  return [...items].sort((a, b) => {
    // Priority first
    const priorityDiff = (priorityOrder[a.priority] || 99) - (priorityOrder[b.priority] || 99);
    if (priorityDiff !== 0) return priorityDiff;

    // Then due_date
    if (!a.due_date && !b.due_date) return 0;
    if (!a.due_date) return 1;
    if (!b.due_date) return -1;
    return a.due_date.localeCompare(b.due_date);
  });
}

/**
 * Priority Section Header
 */
function PrioritySectionHeader({ priority, count }) {
  const labels = {
    P0: { label: 'Critique', bg: 'bg-red-500' },
    P1: { label: 'Important', bg: 'bg-amber-500' },
    P2: { label: 'Normal', bg: 'bg-blue-500' },
    P3: { label: 'Faible', bg: 'bg-gray-400' }
  };

  const config = labels[priority] || labels.P2;

  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border-b border-gray-200">
      <div className={`w-2 h-2 rounded-full ${config.bg}`} />
      <span className="text-sm font-medium text-gray-700">
        {priority} - {config.label}
      </span>
      <span className="text-xs text-gray-500">
        ({count})
      </span>
    </div>
  );
}

/**
 * WorkItemFlatList component
 */
function WorkItemFlatList({ items = [], onAction, onNavigate, onEmail, onSuggestedAction, showPrioritySections = true }) {
  const sortedItems = useMemo(() => sortItems(items), [items]);

  // Group by priority for section headers
  const itemsByPriority = useMemo(() => {
    const groups = { P0: [], P1: [], P2: [], P3: [] };
    sortedItems.forEach(item => {
      const priority = item.priority || 'P2';
      if (groups[priority]) {
        groups[priority].push(item);
      } else {
        groups.P2.push(item);
      }
    });
    return groups;
  }, [sortedItems]);

  if (items.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <ListChecks className="w-12 h-12 mx-auto text-gray-400 mb-3" />
        <p className="text-gray-500">
          Aucune tâche en attente. Bravo !
        </p>
      </div>
    );
  }

  // Simple flat list without sections
  if (!showPrioritySections) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
        {sortedItems.map(item => (
          <WorkItemRow
            key={item.id}
            item={item}
            onAction={onAction}
            onNavigate={onNavigate}
            onEmail={onEmail}
            onSuggestedAction={onSuggestedAction}
          />
        ))}
      </div>
    );
  }

  // List with priority section headers
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {Object.entries(itemsByPriority)
        .filter(([, priorityItems]) => priorityItems.length > 0)
        .map(([priority, priorityItems]) => (
          <div key={priority}>
            <PrioritySectionHeader priority={priority} count={priorityItems.length} />
            <div className="divide-y divide-gray-100">
              {priorityItems.map(item => (
                <WorkItemRow
                  key={item.id}
                  item={item}
                  onAction={onAction}
                  onNavigate={onNavigate}
                  onEmail={onEmail}
                  onSuggestedAction={onSuggestedAction}
                />
              ))}
            </div>
          </div>
        ))}
    </div>
  );
}

export default WorkItemFlatList;
